import { useState, useEffect } from 'react';
import { Box, Typography, Avatar, Chip, Menu, MenuItem, Tooltip, CircularProgress } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { enumApi } from '@/api/enum.api';

function stringToColor(string) {
  if (!string) return '#1976d2';
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

const DEFAULT_STATUSES = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' },
  { label: 'Transferred', value: 'TRANSFERRED' },
  { label: 'Passed Out', value: 'PASSED_OUT' },
  { label: 'Dropped Out', value: 'DROPPED_OUT' },
  { label: 'Deleted', value: 'DELETED' },
];

const getStatusChipColor = (status) => {
  const s = (status || '').toUpperCase();
  if (s === 'ACTIVE') return 'success';
  if (s === 'INACTIVE' || s === 'DELETED') return 'error';
  if (s === 'TRANSFERRED') return 'warning';
  if (s === 'PASSED_OUT') return 'info';
  if (s === 'DROPPED_OUT') return 'default';
  return 'primary';
};

export function StudentProfileHeader({ student: fullData, onStatusChange, updatingStatus }) {
  const student = fullData?.student || {};
  const enrollment = fullData?.enrollment || {};

  const [anchorEl, setAnchorEl] = useState(null);
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);

  useEffect(() => {
    let isMounted = true;
    enumApi.getStudentStatuses()
      .then((res) => {
        if (isMounted && Array.isArray(res) && res.length > 0) {
          setStatuses(res);
        }
      })
      .catch((err) => {
        console.warn('Could not fetch student status enums:', err);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const firstName = student.first_name || student.firstName || '';
  const lastName = student.last_name || student.lastName || '';
  const fullName = student.student_name || `${firstName} ${lastName}`.trim() || 'Unknown Student';
  
  const initial1 = (firstName[0] || fullName[0] || '?').toUpperCase();
  const initial2 = (lastName[0] || (fullName.split(' ')[1]?.[0]) || '').toUpperCase();

  const isRte = student.is_rte !== undefined ? Boolean(student.is_rte) : Boolean(student.isRte);
  const currentStatus = (student.status || 'UNKNOWN').toUpperCase();

  const handleOpenMenu = (e) => {
    if (!updatingStatus) {
      setAnchorEl(e.currentTarget);
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSelectStatus = (newStatus) => {
    handleCloseMenu();
    if (newStatus !== currentStatus && onStatusChange) {
      onStatusChange(newStatus);
    }
  };

  return (
    <WidgetContainer noPadding sx={{ overflow: 'hidden' }}>
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
        <Avatar
          src={student.photo}
          sx={{
            width: 80,
            height: 80,
            bgcolor: stringToColor(student.admission_no || student.id || student.student_id || ''),
            color: '#fff',
            fontSize: '2.5rem',
            fontWeight: 600,
          }}
        >
          {initial1}{initial2}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="h5" fontWeight={600} color="text.primary">
              {fullName}
            </Typography>

            <Tooltip title="Click to change status">
              <Chip
                label={currentStatus}
                color={getStatusChipColor(currentStatus)}
                onClick={handleOpenMenu}
                onDelete={handleOpenMenu}
                deleteIcon={updatingStatus ? <CircularProgress size={12} color="inherit" /> : <ChevronDown size={14} />}
                disabled={updatingStatus}
                sx={{
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  height: 24,
                  cursor: updatingStatus ? 'wait' : 'pointer',
                  '& .MuiChip-deleteIcon': {
                    color: 'inherit',
                    opacity: 0.8,
                  },
                }}
              />
            </Tooltip>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
              slotProps={{
                paper: {
                  elevation: 3,
                  sx: { borderRadius: 2, minWidth: 150 }
                }
              }}
            >
              {statuses.map((opt) => (
                <MenuItem
                  key={opt.value}
                  selected={currentStatus === opt.value}
                  onClick={() => handleSelectStatus(opt.value)}
                  sx={{ fontSize: '0.875rem', fontWeight: currentStatus === opt.value ? 700 : 500 }}
                >
                  {opt.label || opt.value}
                </MenuItem>
              ))}
            </Menu>

            {isRte && (
              <Chip
                label="RTE"
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  height: 22,
                  backgroundColor: '#7C3AED',
                  color: '#FFFFFF',
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" fontWeight={600} mr={0.5}>Adm No:</Box> {student.admission_no || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" fontWeight={600} mr={0.5}>SR No:</Box> {student.sr_number || student.sr_no || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" fontWeight={600} mr={0.5}>Class:</Box> {enrollment.class?.class_name || enrollment.class_id?.class_name || enrollment.class_id || '-'} - {enrollment.section?.section_name || enrollment.section_id?.section_name || enrollment.section_id || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" fontWeight={600} mr={0.5}>Roll No:</Box> {enrollment.roll_number || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" fontWeight={600} mr={0.5}>RTE:</Box> {isRte ? 'Yes' : 'No'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </WidgetContainer>
  );
}
